import { Injectable, inject } from '@angular/core';

import { TODO } from '@b2b/types/core/utils';

import { HelpersService } from '../helpers/helpers.service';
import { FILTER_PORT, type IFilterPort } from '../utils/filter.port';
import { AuditTrailColumnGenerator } from './audit-trail-column-generator.service';
import { AuditTrailConfigsService } from './audit-trail-configs.service';
import { AuditTrailGridsService } from './audit-trail-grids.service';
import { AuditTrailVersionHandlerService } from './audit-trail-version-handler.service';

/**
 * AuditTrailService — Angular 18.
 *
 * Ported 1:1 from apps/b2b-ui/common/services/auditTrail/auditTrail.service.js
 * (legacy AngularJS name 'AuditTrailService').
 *
 * Orchestrates building a single audit-trail "version" view: processes the diff
 * object into field tables and image groups, builds the diff grids (via
 * AuditTrailGridsService) and their ag-grid column options (via
 * AuditTrailColumnGenerator), and holds the resulting `version` model that the
 * audit-trail directive renders.
 *
 * Registered back into AngularJS under 'AuditTrailService' via
 * downgradeInjectable in app/upgrade/ng-downgraded-components.ts.
 */
@Injectable({ providedIn: 'root' })
export class AuditTrailService {
  private config = inject(AuditTrailConfigsService);
  private versionHandler = inject(AuditTrailVersionHandlerService);
  private grids = inject(AuditTrailGridsService);
  private columnGenerator = inject(AuditTrailColumnGenerator);
  private helpers = inject(HelpersService);
  private $filter = inject<IFilterPort>(FILTER_PORT);

  ID = '';
  version: TODO = {};
  path = '';
  tempGridObj: TODO = {
    headers: [],
    updated: [],
    original: [],
    newValues: [],
    removed: [],
  };

  init(id: TODO, model: TODO, path: TODO): void {
    this.ID = id;
    this.config.init(path);
    this.path = path;
    this.setVersionModel(model);
  }

  setVersionModel(model: TODO): void {
    // rename addOn in combo history to avoid type conflict
    if (/combo.event.template/.test(model.auditTrail.businessEvent)) {
      if (model.patchedDiff.addOns) {
        model.patchedDiff.addOnsCombo = model.patchedDiff.addOns;
        delete model.patchedDiff.addOns;
      }

      if (model.actual.addOns) {
        model.actual.addOnsCombo = model.actual.addOns;
        delete model.actual.addOns;
      }
    }

    if (model.diff === null) {
      this.versionHandler.setNewVersion(model);
      this.version = this.versionHandler.getVersion();
      this.version.isEmpty = true;
    } else {
      this.versionHandler.setNewVersion(model);
      this.version = this.versionHandler.getVersion();
      this.version.isEmpty = false;
      this.processDiffObject();
      this.processGrids();
      this.setGridOptions();
    }
  }

  setGridOptions(): void {
    let key: string;
    let builtGridOption: TODO;
    let options: TODO = Object.create(null);

    if (this.version.grids.length) {
      for (let i = 0, ii = this.version.grids.length; i < ii; i++) {
        options = this.generateGridColumns();
      }
      for (key in options) {
        builtGridOption = this.config.buildGridOptions(options[key]);
        this.version.gridOptions.push(builtGridOption);
      }
    }
  }

  getVersionModel(): TODO {
    return this.version;
  }

  generateGridColumns(): TODO {
    const columns: TODO = {};

    for (let k = 0, kk = this.version.grids.length; k < kk; k++) {
      for (let i = 0, ii = this.version.grids[k].headers.length; i < ii; i++) {
        if (!columns[k]) {
          columns[k] = [];
        }
        columns[k].push({
          headerName: this.version.grids[k].headers[i],
          valueGetter: 'data[col' + [i] + ']',
          cellStyle: this.columnGenerator.getCellStyle(),
          cellRenderer: this.columnGenerator.getCellRenderer(),
        });
      }
    }

    return columns;
  }

  processDiffObject(): void {
    const groups = this.filterDiffGroups(this.version.diff.groups);
    let group: TODO;

    for (let i = 0, ii = groups.length; i < ii; i++) {
      group = groups[i];
      if (group.fields) {
        this.processFields(group);
      }
      if (group.images) {
        this.processImages(group);
      }
    }
  }

  filterDiffGroups(groups: TODO): TODO[] {
    let r: TODO[] = [];
    if (groups.length) {
      r = groups.filter(function (item: TODO) {
        // schedules comes from patchedDiff, and needs to be removed from the
        // diff prop to avoid info redundance
        if (item.name !== 'schedule') {
          return item;
        }
      });
    }
    return r;
  }

  processFields(group: TODO): void {
    const fields = group.fields;
    let option: TODO;
    let field: TODO;

    for (let i = 0, ii = fields.length; i < ii; i++) {
      option = fields[i].op;
      field = fields[i];
      if (option === 'add') {
        group.fields[i].oldValue = 'New';
        group.fields[i].value = this.processFieldValue(field.value);
      } else if (this.helpers.validISODate(field.oldValue)) {
        group.fields[i].value = this.$filter('date')(field.value, this.helpers.dateFormats.fieldDate);
        group.fields[i].oldValue = this.$filter('date')(field.oldValue, this.helpers.dateFormats.fieldDate);
      }
    }
    this.versionHandler.addToVersion('tables', group);
  }

  processFieldValue(value: TODO): TODO {
    let r: TODO = value;

    if (typeof value === 'object') {
      r = this.iterateFieldValueObj(value).join(', ');
    }

    if (this.helpers.validISODate(value)) {
      r = this.$filter('date')(value, this.helpers.dateFormats.fieldDate);
    }

    return r;
  }

  iterateFieldValueObj(obj: TODO): TODO[] {
    let r: TODO[] = [];

    for (const property in obj) {
      if (obj.hasOwnProperty(property)) {
        if (typeof obj[property] === 'object') {
          r = this.iterateFieldValueObj(obj[property]);
        } else {
          r.push(property + ': ' + obj[property]);
        }
      }
    }

    return r;
  }

  processImages(group: TODO): void {
    const images = group.images;
    let headers: TODO;
    let idxPrimary: number;

    for (let i = 0, ii = images.length; i < ii; i++) {
      headers = images[i].headers;
      idxPrimary = headers.indexOf('Primary');

      if (images[i].deletedValue.length) {
        this.processImageValues(images[i].deletedValue, images[i], idxPrimary, 'removed');
      }
      if (images[i].updatedValues.length) {
        this.processImageValues(images[i].updatedValues, images[i], idxPrimary, 'replaced');
      }
      if (images[i].newValues.length) {
        this.processImageValues(images[i].newValues, images[i], idxPrimary, 'newValues');
      }
      this.versionHandler.addToVersion('images', {
        original: this.tempGridObj.original,
        updated: this.tempGridObj.updated,
        newValues: this.tempGridObj.newValues,
        removed: this.tempGridObj.removed,
        breadcrumb: group.breadcrumb,
      });
      this.resetTempGrid();
    }
  }

  processImageValues(dataset: TODO, grid: TODO, idx: number, status: TODO): void {
    for (let i = 0, ii = dataset.length; i < ii; i++) {
      if (status === 'removed') {
        this.tempGridObj.removed.push({
          id: grid.oldValues[dataset[i]][0],
          preview: grid.oldValues[dataset[i]].pop(),
          primary: idx <= 0 ? grid.oldValues[dataset[i]][idx] : false,
        });
      }
      if (status === 'newValues') {
        this.tempGridObj.newValues.push({
          id: dataset[i][0],
          preview: dataset[i].pop(),
          primary: idx <= 0 ? dataset[i][idx] : false,
        });
      }
      if (status === 'replaced') {
        this.tempGridObj.newValues.push({
          id: grid.updatedValues[i][0],
          preview: grid.updatedValues[i].pop(),
          primary: idx <= 0 ? dataset[i][idx] : false,
        });
      }
    }
  }

  processGrids(): void {
    let type: TODO;
    let updated: TODO;
    let original: TODO;
    const _updated = this.version.updated;
    const _original = this.version.original;
    const keys = Object.keys(this.version.patchedDiff);

    for (let i = 0, ii = keys.length; i < ii; i++) {
      type = keys[i];
      if (this.config.storage[type]) {
        updated = _updated[type];
        original = _original[type];
        this.grids.processGridType(updated, original, type);
      }
    }
  }

  resetTempGrid(): void {
    this.tempGridObj.original = [];
    this.tempGridObj.updated = [];
    this.tempGridObj.newValues = [];
    this.tempGridObj.removed = [];
  }

  resetVersionModel(): void {
    this.version = {};
    this.versionHandler.reset();
  }
}
